import { motion } from 'framer-motion';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import formatNumber from '../../utils/formatNumber';
import { XP_LEVELS } from '@/config/kittyLevels';

const MAX_LEVEL = XP_LEVELS[XP_LEVELS.length - 1].level;

const LEVEL_COLORS = [
  '#6b7280',
  '#84C341',
  '#22c55e',
  '#00C49F',
  '#0088FE',
  '#3b82f6',
  '#8b5cf6',
  '#FFBB28',
  '#FF8042',
  '#ef4444',
];

function getLevelColor(level) {
  return LEVEL_COLORS[(level - 1) % LEVEL_COLORS.length];
}

function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const color = getLevelColor(data.level);
    return (
      <div
        className="px-4 py-3 rounded-lg"
        style={{
          background: 'rgba(23,23,23,0.95)',
          border: `1px solid ${color}`,
          boxShadow: `0 0 20px ${color}40`,
        }}
      >
        <p className="text-neutral-200 font-bold">Level {data.level}</p>
        <p style={{ color }} className="text-lg font-black">
          {formatNumber(data.count)} citizens
        </p>
        <p className="text-neutral-500 text-sm">{data.percentage}% of population</p>
      </div>
    );
  }
  return null;
}

function LevelBar({ level, count, maxCount, percentage, index, totalCitizens }) {
  const color = getLevelColor(level);
  const widthPercentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="flex items-center gap-2 lg:gap-4 group"
    >
      <div
        className="w-8 lg:w-12 h-8 lg:h-10 flex items-center justify-center rounded-lg font-black text-sm lg:text-lg shrink-0"
        style={{
          background: `${color}20`,
          color: color,
          border: `1px solid ${color}40`,
          boxShadow: `0 0 15px ${color}20`,
        }}
      >
        {level}
      </div>

      <div className="flex-1 relative">
        <div className="h-8 lg:h-10 rounded-lg overflow-hidden" style={{ background: 'rgba(38,38,38,0.6)' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${widthPercentage}%` }}
            transition={{ duration: 0.8, delay: 0.2 + index * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-lg relative overflow-hidden"
            style={{
              background: `linear-gradient(90deg, ${color}80 0%, ${color} 100%)`,
              boxShadow: `0 0 20px ${color}40`,
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
              }}
            />

            <motion.div
              className="absolute inset-y-0 w-20 hidden lg:block"
              initial={{ left: '-20%' }}
              animate={{ left: '120%' }}
              transition={{
                duration: 1.5,
                delay: 1 + index * 0.1,
                repeat: Infinity,
                repeatDelay: 3,
              }}
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              }}
            />
          </motion.div>
        </div>

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <span className="text-neutral-400 text-xs lg:text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity hidden lg:block">
            {percentage}%
          </span>
        </div>
      </div>

      <div className="w-14 lg:w-20 text-right shrink-0">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 + index * 0.08 }}
          className="font-bold text-neutral-300 text-sm lg:text-base"
        >
          {formatNumber(count)}
        </motion.span>
      </div>
    </motion.div>
  );
}

export default function CensusLevelPyramid({ citizensByLevel }) {
  if (!citizensByLevel || citizensByLevel.length === 0) return null;

  const totalCitizens = citizensByLevel.reduce((acc, l) => acc + l.count, 0);
  const maxCount = Math.max(...citizensByLevel.map((l) => l.count));

  const dataWithPercentage = citizensByLevel.map((l) => ({
    ...l,
    percentage: totalCitizens > 0 ? ((l.count / totalCitizens) * 100).toFixed(1) : '0',
  }));

  const avgLevel =
    totalCitizens > 0
      ? (citizensByLevel.reduce((acc, l) => acc + l.level * l.count, 0) / totalCitizens).toFixed(1)
      : 0;

  return (
    <section className="py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <h2 className="text-2xl font-black text-neutral-200 tracking-tight uppercase">
          Citizen Levels
        </h2>
        <p className="text-neutral-500 mt-2">
          Distribution across {formatNumber(totalCitizens)} active kitties
        </p>
        <div className="mt-3 mx-auto w-16 h-1 rounded-full bg-gradient-to-r from-[#84C341] via-[#0088FE] to-[#ef4444]" />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {[
          { label: 'Average Level', value: avgLevel, color: '#0088FE' },
          { label: 'Most Common', value: `Level ${dataWithPercentage.sort((a, b) => b.count - a.count)[0]?.level || 1}`, color: '#84C341' },
          { label: 'Max Level Citizens', value: formatNumber(citizensByLevel.find((l) => l.level === MAX_LEVEL)?.count || 0), color: '#ef4444' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="text-center p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(38,38,38,0.6) 0%, rgba(23,23,23,0.8) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <p className="text-neutral-500 text-sm uppercase tracking-wide">{stat.label}</p>
            <p className="text-2xl font-black mt-1" style={{ color: stat.color }}>
              {stat.value}
            </p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="rounded-2xl p-6"
        style={{
          background: 'linear-gradient(135deg, rgba(38,38,38,0.5) 0%, rgba(23,23,23,0.8) 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="space-y-3">
          {dataWithPercentage
            .sort((a, b) => a.level - b.level)
            .map((data, index) => (
              <LevelBar
                key={data.level}
                level={data.level}
                count={data.count}
                maxCount={maxCount}
                percentage={data.percentage}
                index={index}
                totalCitizens={totalCitizens}
              />
            ))}
        </div>
      </motion.div>

      <div className="hidden lg:block mt-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="rounded-2xl p-4"
          style={{
            background: 'linear-gradient(135deg, rgba(38,38,38,0.5) 0%, rgba(23,23,23,0.8) 100%)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dataWithPercentage.sort((a, b) => a.level - b.level)} barCategoryGap="20%">
              <XAxis
                dataKey="level"
                tick={{ fill: '#a3a3a3', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                tickFormatter={(v) => `Lvl ${v}`}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {dataWithPercentage.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getLevelColor(entry.level)} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </section>
  );
}
